import { type Category } from '@koala/sdk';
import { useRouter } from 'next/router';
import { useEffect, useMemo, useRef } from 'react';
import styled from 'styled-components';
import { useDispatch, useSelector } from '@/redux';
import { useConfigOverride } from '@/redux/cmsConfig';
import { getFontStyles } from '@/redux/cmsConfig/utils';
import tagsActions from '@/redux/tags/actions';
import { getMenuFilterTags } from '@/utils/menu';

interface Props {
  categories: Category[];
}

/** Tag ids from a `?filter=1,2` query value. */
const parseFilterQuery = (filter: string | string[] | undefined) =>
  (Array.isArray(filter) ? filter.join(',') : filter ?? '')
    .split(',')
    .map((value) => Number(value))
    .filter((id) => Number.isInteger(id) && id > 0);

/**
 * Dietary filter for non-Food-Hall menus: one toggle chip per filter tag on the menu. Selected
 * tags narrow the menu to products carrying all of them (see `filterProductsByTagId`).
 */
export function DietaryFilter({ categories }: Props) {
  const { food_hall: foodHallEnabled } = useConfigOverride('admin');

  // Food Hall stores drive the same selection with their own single-tag vendor nav.
  if (foodHallEnabled) {
    return null;
  }

  return <DietaryFilterChips categories={categories} />;
}

function DietaryFilterChips({ categories }: Props) {
  const dispatch = useDispatch();
  const router = useRouter();
  const activeTagIds = useSelector((state) => state.app.tags.activeTagIds);
  const hydratedFromUrl = useRef(false);

  const menuTags = useMemo(() => getMenuFilterTags(categories), [categories]);
  const urlFilter = router.query.filter;

  // Keep the selection to tags on this menu: pick up a shared `?filter=` once, and drop ids
  // carried over from another store's menu — including one with no tags, where no chips render
  // to clear them. Wait for the menu to load so a selection isn't dropped mid-switch.
  useEffect(() => {
    if (!categories.length || !router.isReady) {
      return;
    }

    const requested = activeTagIds ?? [];
    const fromUrl = hydratedFromUrl.current ? [] : parseFilterQuery(urlFilter);
    hydratedFromUrl.current = true;

    const menuTagIds = menuTags.map((tag) => tag.id);
    const kept = [...new Set([...requested, ...fromUrl])].filter((id) => menuTagIds.includes(id));

    if (kept.length === requested.length && kept.every((id) => requested.includes(id))) {
      return;
    }

    dispatch(tagsActions.clearActiveTags());
    kept.forEach((id) => dispatch(tagsActions.toggleActiveTag(id)));
  }, [categories.length, menuTags, activeTagIds, urlFilter, router.isReady, dispatch]);

  if (!menuTags.length) {
    return null;
  }

  const hasSelection = Boolean(activeTagIds?.length);

  return (
    <Container role="group" aria-label="Filter menu by dietary preference">
      {menuTags.map((tag) => {
        const isSelected = Boolean(activeTagIds?.includes(tag.id));

        return (
          <Chip
            key={tag.id}
            type="button"
            aria-pressed={isSelected}
            $isSelected={isSelected}
            onClick={() => dispatch(tagsActions.toggleActiveTag(tag.id))}
          >
            {tag.label}
          </Chip>
        );
      })}

      {hasSelection && (
        <ClearButton type="button" onClick={() => dispatch(tagsActions.clearActiveTags())}>
          Clear filters
        </ClearButton>
      )}
    </Container>
  );
}

const Container = styled.div({
  alignItems: 'center',
  display: 'flex',
  flexWrap: 'wrap',
  gap: 'var(--size-2)',
});

const Chip = styled.button<{ $isSelected: boolean }>(({ theme, $isSelected }) => {
  const tag = theme.dietary_preferences_tag;

  return {
    backgroundColor:
      ($isSelected ? tag.active_background_color : null) ?? tag.background_color ?? 'transparent',
    border: '1px solid',
    borderColor:
      ($isSelected ? tag.active_border_color : null) ?? tag.border_color ?? 'transparent',
    borderRadius: tag.border_radius ?? 0,
    cursor: 'pointer',
    fontWeight: $isSelected ? 700 : 500,
    minHeight: '2.25rem',
    padding: 'var(--size-1) var(--size-3)',
    ...getFontStyles(tag.font, {
      color: (value) => ($isSelected ? tag.active_font_color ?? value : value),
      line_height: () => '1',
    }),
  };
});

const ClearButton = styled.button(({ theme }) => ({
  background: 'none',
  border: 'none',
  cursor: 'pointer',
  minHeight: '2.25rem',
  padding: 'var(--size-1) var(--size-2)',
  textDecoration: 'underline',
  ...getFontStyles(theme.dietary_preferences_tag.font, ['color', 'font_family', 'font_size']),
}));
